import * as formatters from '../../../utils/format/partnerRegistrationFormatters';
import {
    accountNumberRegExp,
    bikRegExp,
    dateRegExp,
    excludeSymbolsRegExp,
    indexRegExp,
    innOgrnRegExp,
    passportRegExp,
    snilsNumberRegExp,
    subdivisionCodeRegExp,
} from '../../../utils/regexps';
import { composeValidators } from '../../../utils/validators/compose';
import { makeMaxLengthValidator } from '../../../utils/validators/makeMaxLengthValidator';
import { makeRegExpValidator } from '../../../utils/validators/makeRegExpValidator';
import { makeRequiredValidator } from '../../../utils/validators/makeRequiredValidator';
import { PartnerType } from '../PartnerTypeTabs/types';
import { FieldProps } from './types';

const validateName = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
    makeMaxLengthValidator(25, 'Строка не должна превышать 25 символов'),
);

const validateDate = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(dateRegExp, 'Укажите дату в формате 00.00.0000'),
);

const validatePassport = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(passportRegExp, 'Укажите данные в формате 00 00 000000'),
);

const validateSubdivisionCode = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(subdivisionCodeRegExp, 'Укажите данные в формате 000000'),
);

const validateIndex = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(indexRegExp, 'Укажите данные в формате 000000'),
);

const validateSnilsNumber = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(snilsNumberRegExp, 'Укажите данные в формате 000 000 000 00'),
);

const validateInnOgrn = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(innOgrnRegExp, 'Укажите корректный номер'),
);

const validateRequired = composeValidators(makeRequiredValidator<string>());

const validateBik = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(bikRegExp, 'БИК должен содержать 9 цифр'),
);

const validateAccountNumber = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(accountNumberRegExp, 'Укажите данные в формате 0000 0000 0000 0000 0000'),
);

const validateBankName = makeRequiredValidator<string>('Банк не найден');

const extractDigits = (value: string) => value.replace(/\D/g, '');
const extractLetters = (value: string) => value.replace(/[^a-zA-Zа-яА-Я\s-]/g, '');

export const SE_FIELDS: FieldProps[] = [
    {
        type: 'text',
        name: 'lastName',
        label: 'Фамилия',
        placeholder: 'Иванов',
        required: true,
        validate: validateName,
        formatter: formatters.formatLettersOnly,
        valueExtractor: extractLetters,
    },
    {
        type: 'text',
        name: 'firstName',
        label: 'Имя',
        placeholder: 'Иван',
        required: true,
        validate: validateName,
        formatter: formatters.formatLettersOnly,
        valueExtractor: extractLetters,
    },

    {
        type: 'text',
        name: 'patronymic',
        label: 'Отчество',
        placeholder: 'Иванович',
        required: true,
        validate: validateName,
        formatter: formatters.formatLettersOnly,
        valueExtractor: extractLetters,
    },
    {
        type: 'date',
        name: 'birthDate',
        label: 'Дата рождения',
        placeholder: '26.07.1999',
        required: true,
        validate: validateDate,
    },
    {
        type: 'text',
        name: 'passport',
        label: 'Серия и номер паспорта',
        placeholder: '00 00 000000',
        required: true,
        validate: validatePassport,
        formatter: formatters.formatPassport,
        valueExtractor: extractDigits,
    },
    {
        type: 'text',
        name: 'issuedBy',
        label: 'Кем выдан',
        placeholder: 'ГУ МВД России по г. Москве',
        required: true,
        validate: validateRequired,
    },
    {
        type: 'date',
        name: 'issuedDate',
        label: 'Дата выдачи',
        placeholder: '00.00.0000',
        required: true,
        validate: validateDate,
    },
    {
        type: 'text',
        name: 'subdivisionCode',
        label: 'Код подразделения',
        placeholder: '000000',
        required: true,
        validate: validateSubdivisionCode,
        formatter: formatters.formatSubdivisionCode,
        valueExtractor: extractDigits,
    },
    {
        type: 'text',
        name: 'registrationAddress',
        label: 'Адрес регистрации',
        placeholder: 'г. Москва, улица Льва Толстого, дом 16',
        required: true,
        validate: validateRequired,
    },
    {
        type: 'text',
        name: 'index',
        label: 'Индекс',
        placeholder: '000000',
        required: true,
        validate: validateIndex,
        formatter: formatters.formatIndex,
        valueExtractor: extractDigits,
    },
    {
        type: 'text',
        name: 'snilsNumber',
        label: 'Номер СНИЛС',
        placeholder: '000 000 000 00',
        required: true,
        validate: validateSnilsNumber,
        formatter: formatters.formatSnils,
        valueExtractor: extractDigits,
    },
];

export const IE_FIELDS: FieldProps[] = [
    {
        type: 'dropdown',
        required: true,
        name: 'taxSystem',
        label: 'Система налогообложения',
        values: ['Без НДС', '5%', '7%', '20%'],
    },
    {
        type: 'text',
        name: 'registrationAddress',
        label: 'Адрес регистрации',
        placeholder: 'г. Москва, улица Льва Толстого, дом 16',
        required: true,
        validate: validateRequired,
    },
    {
        type: 'text',
        name: 'index',
        label: 'Индекс',
        placeholder: '000000',
        required: true,
        validate: validateIndex,
        formatter: formatters.formatIndex,
        valueExtractor: extractDigits,
    },
    {
        type: 'text',
        name: 'innOgrn',
        label: 'ИНН или ОГРН',
        placeholder: '7729355614',
        required: true,
        validate: validateInnOgrn,
        formatter: formatters.formatInnOgrn,
        valueExtractor: extractDigits,
    },
];

export const LLC_FIELDS: FieldProps[] = [
    {
        type: 'dropdown',
        required: true,
        name: 'taxSystem',
        label: 'Система налогообложения',
        values: ['Без НДС', '5%', '7%', '20%'],
    },
    {
        type: 'text',
        name: 'innOgrn',
        label: 'ИНН или ОГРН',
        placeholder: '7729355614',
        required: true,
        validate: validateInnOgrn,
        formatter: formatters.formatInnOgrn,
        valueExtractor: extractDigits,
    },
];

export const SIGNER_DATA_FIELDS: FieldProps[] = [
    {
        type: 'text',
        required: true,
        name: 'fullName',
        label: 'Фамилия, имя и отчество',
        placeholder: 'Иванов Иван Иванович',
        validate: validateRequired,
        formatter: formatters.formatLettersOnly,
        valueExtractor: extractLetters,
    },
    {
        type: 'text',
        required: true,
        name: 'position',
        label: 'Должность',
        placeholder: 'Генеральный директор',
        validate: validateRequired,
    },
];

export const BANK_DETAILS_FIELDS: FieldProps[] = [
    {
        type: 'text',
        name: 'bik',
        label: 'БИК',
        placeholder: '000000000',
        required: true,
        validate: validateBik,
        formatter: formatters.formatBik,
        valueExtractor: extractDigits,
    },
    {
        type: 'text',
        name: 'bankName',
        label: 'Название банка',
        placeholder: 'Поле автозаполняется',
        required: true,
        disabled: true,
        validate: validateBankName,
    },
    {
        type: 'text',
        name: 'accountNumber',
        label: 'Расчетный счет',
        placeholder: '0000 0000 0000 0000 0000',
        required: true,
        validate: validateAccountNumber,
        formatter: formatters.formatAccountNumber,
        valueExtractor: extractDigits,
    },
];

export const fieldsMap = {
    [PartnerType.SE]: SE_FIELDS,
    [PartnerType.IE]: IE_FIELDS,
    [PartnerType.LLC]: LLC_FIELDS,
    signerData: SIGNER_DATA_FIELDS,
    bankDetails: BANK_DETAILS_FIELDS,
};
